import React from 'react';
import { Box, Button, ButtonGroup, Card, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import type { RenterMoveInCostsDetailsInterface } from '@renter-hub/models/Types';
import { MoveInCostsCTA } from './MoveInCostsCTA';
import MoveInCostsDetails from './MoveInCostsDetails';
import TransactionConstants from '@common/constants/TransactionConstants';
import { DashboardSubheading } from '@common/components/DashboardSubheading/DashboardSubheading.component';
import { MOVE_IN_COSTS_PAYMENT } from '@renter-hub/constants/paymentConstants';
import { logError } from '@zg-rentals/log-error';

interface Props {
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
  moveInCostsSubheading: { statement: Array<any>; title: string };
  goToEditMoveInCostsPage: () => any;
  goToEditPaymentInfoPage: (typeOfPaymentToMake?: typeof MOVE_IN_COSTS_PAYMENT) => any;
  goToCancelMoveInCostsPage: () => any;
  displayHiddenText?: boolean;
}

interface State {
  expandOffline: boolean;
}

class MoveInCostsCard extends React.Component<Props, State> {
  static defaultProps = {
    moveInCostsDetails: {
      baseAmount: 0,
      dueDate: '',
      isScheduled: false,
      items: [],
      totalAmount: 0,
      zgFee: 0,
      status: '',
      sendDate: '',
      groupKey: '',
    },
    moveInCostsSubheading: { statement: [], title: 'Move-in payment' },
    goToEditMoveInCostsPage: () => {},
    goToCancelMoveInCostsPage: () => {},
    goToEditPaymentInfoPage: () => {},
  };

  state = {
    expandOffline: false,
  };

  // Paid Offline
  toggleExpand = (): void => {
    this.setState({ expandOffline: !this.state.expandOffline });
  };

  renderMoveInCostsHeading = () => {
    const { moveInCostsSubheading } = this.props;
    return (
      <>
        <Heading className="pvxs" level={2} textStyle="heading-xs">
          {moveInCostsSubheading!.title}
        </Heading>
        <DashboardSubheading {...this.props.moveInCostsSubheading} />
      </>
    );
  };

  renderPaidOfflineSection = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <TextButton textStyle="body-sm" onClick={this.toggleExpand}>
        Did you pay this offline?
      </TextButton>
      {this.state.expandOffline && (
        <Paragraph className="ptm" textStyle="body-sm" css={{ color: 'text.subtle', textAlign: 'center' }}>
          If you&apos;ve taken care of this payment, please contact your landlord to cancel it.
        </Paragraph>
      )}
    </Box>
  );

  // getMoveInCostsUnpaid
  renderUnpaidMoveInCosts = () => {
    const { moveInCostsDetails } = this.props;

    /* scheduled */
    if (moveInCostsDetails.isScheduled) {
      return (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 'default',
          }}
        >
          <Box>
            {this.renderMoveInCostsHeading()}
            <TextButton onClick={this.props.goToCancelMoveInCostsPage}>Cancel payment</TextButton>
          </Box>
          <Box>
            <MoveInCostsDetails {...this.props} />
          </Box>
        </Box>
      );
    }

    /* not scheduled */
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: '1fr',
            lg: '7fr 5fr',
          },
          gap: 'default',
        }}
      >
        <Box>{this.renderMoveInCostsHeading()}</Box>
        <Box>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <MoveInCostsCTA onClick={this.props.goToEditMoveInCostsPage} />
            {this.renderPaidOfflineSection()}
          </Box>
        </Box>
        <Box css={{ gridColumn: '1 / -1' }}>
          <MoveInCostsDetails {...this.props} />
        </Box>
      </Box>
    );
  };

  // getMoveInCostsFailed
  renderMoveInCostsRetryButtons = () => (
    <ButtonGroup orientation="vertical" aria-label="Try payment again">
      <Button fluid={true} onClick={this.props.goToEditMoveInCostsPage}>
        Try payment again
      </Button>
      <Button fluid={true} onClick={() => this.props.goToEditPaymentInfoPage(MOVE_IN_COSTS_PAYMENT)}>
        Change payment method
      </Button>
    </ButtonGroup>
  );

  renderFailedMoveInCosts = () => {
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: '1fr',
            lg: '7fr 5fr',
          },
          gap: 'default',
        }}
      >
        <Box>{this.renderMoveInCostsHeading()}</Box>
        <Box>
          {this.renderMoveInCostsRetryButtons()}
          {this.renderPaidOfflineSection()}
        </Box>
        <Box css={{ gridColumn: '1 / -1' }}>
          <MoveInCostsDetails {...this.props} />
        </Box>
      </Box>
    );
  };

  renderPastDueMoveInCosts = () => {
    const { moveInCostsDetails } = this.props;

    /* scheduled */
    if (moveInCostsDetails.isScheduled) {
      return (
        <>
          {this.renderMoveInCostsHeading()}
          <MoveInCostsDetails {...this.props} />
        </>
      );
    }

    /* not scheduled */
    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: {
            base: '1fr',
            lg: '7fr 5fr',
          },
          gap: 'default',
        }}
      >
        <Box>{this.renderMoveInCostsHeading()}</Box>
        <Box>
          <Box css={{ display: 'flex', flexDirection: 'column' }}>
            <MoveInCostsCTA onClick={this.props.goToEditMoveInCostsPage} />
            {this.renderPaidOfflineSection()}
          </Box>
        </Box>
        <Box css={{ gridColumn: '1 / -1' }}>
          <MoveInCostsDetails {...this.props} />
        </Box>
      </Box>
    );
  };

  renderError = () => {
    logError({
      error: new Error('Invalid move-in costs transaction renterStatus'),
      context: {
        moveInCostsDetails: this.props.moveInCostsDetails,
      },
    });

    return this.renderMoveInCostsHeading();
  };

  renderDefault = () => {
    return (
      <>
        {this.renderMoveInCostsHeading()}
        <MoveInCostsDetails {...this.props} />
      </>
    );
  };

  render(): React.ReactNode {
    const { moveInCostsDetails } = this.props;
    const { status } = moveInCostsDetails;

    let body;

    if (status === TransactionConstants.STATUS.UNPAID) {
      body = this.renderUnpaidMoveInCosts();
    } else if (
      /* past or future failed */
      status === TransactionConstants.STATUS.PAYMENT_FAILED ||
      status === TransactionConstants.STATUS.DEPOSIT_FAILED
    ) {
      body = this.renderFailedMoveInCosts();
    } else if (status === TransactionConstants.STATUS.PAST_DUE) {
      body = this.renderPastDueMoveInCosts();
    } else if (this.props.displayHiddenText) {
      body = this.renderDefault();
    } else {
      body = this.renderError();
    }

    return (
      <Card css={{ padding: 'default', marginBottom: 'loose' }} elevated={false}>
        {body}
      </Card>
    );
  }
}

export default MoveInCostsCard;
